'use client';

import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { api } from '@/lib/api';
import { getRoleLabel } from '@/lib/access';
import { useStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { AttentionZoneWidget } from '@/components/analytics/attention-zone-widget';
import { ShiftsAttentionWidget } from '@/components/analytics/shifts-attention-widget';
import { ChecklistProgressWidget } from '@/components/analytics/checklist-progress-widget';
import { DashboardKpiStrip } from '@/components/analytics/dashboard-kpi-strip';
import { LocationHealthGrid } from '@/components/analytics/location-health-grid';
import { NetworkHealthHero } from '@/components/analytics/network-health-hero';
import { PeopleHealthWidget } from '@/components/analytics/people-health-widget';
import { ShiftFunnelWidget } from '@/components/analytics/shift-funnel-widget';
import type { AnalyticsHubData } from '@/components/analytics/analytics-dashboard-types';

const PERIODS = [
  { key: 'today', label: 'Сегодня' },
  { key: '7d', label: '7 дней' },
  { key: '30d', label: '30 дней' },
] as const;

export default function DashboardPage() {
  const { user, selectedLocationId, locations } = useStore();
  const selectedLocationName = selectedLocationId
    ? locations.find((location) => location.id === selectedLocationId)?.name || null
    : null;
  const [period, setPeriod] = useState<(typeof PERIODS)[number]['key']>('today');
  const [data, setData] = useState<AnalyticsHubData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getAnalyticsHub({
        period,
        location_id: selectedLocationId || undefined,
      });
      setData(result as AnalyticsHubData);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить дашборд');
    } finally {
      setLoading(false);
    }
  }, [period, selectedLocationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-6xl">
        <div className="space-y-3 pb-24 sm:space-y-4">
          <div className="h-64 animate-pulse rounded-3xl bg-[var(--app-surface-strong)]" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="h-32 animate-pulse rounded-3xl bg-[var(--app-surface-strong)]" />
            ))}
          </div>
          <div className="h-80 animate-pulse rounded-3xl bg-[var(--app-surface-strong)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl">
      <div className="space-y-4 pb-24 sm:space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-bold leading-tight text-[var(--app-text)] sm:text-2xl">Дашборд сети</h1>
            <p className="mt-1 text-sm text-[var(--app-text-soft)]">
              {user ? `${getRoleLabel(user.role, { is_organization_owner: user.is_organization_owner })}: ` : ''}
              {selectedLocationName
                ? `фокус на точке ${selectedLocationName}`
                : 'визуальное состояние точек, смен, чек-листов и команды'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] p-1 shadow-sm">
              {PERIODS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setPeriod(item.key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    period === item.key
                      ? 'bg-primary-600 text-white'
                      : 'text-[var(--app-text-soft)] hover:text-[var(--app-text)]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Обновить
            </Button>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-600 px-4 py-3 text-sm text-white">
            {error}
          </div>
        ) : null}

        {data ? (
          <>
            <NetworkHealthHero data={data} />
            <DashboardKpiStrip items={data.kpis} />
            <AttentionZoneWidget attention={data.attention} />
            <ShiftsAttentionWidget />
            <LocationHealthGrid locations={data.locations} />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ShiftFunnelWidget shifts={data.shifts} />
              <ChecklistProgressWidget checklists={data.checklists} />
              <PeopleHealthWidget people={data.people} />
            </div>
          </>
        ) : (
          <div className="rounded-3xl border border-[var(--app-border)] bg-[var(--app-surface)] py-16 text-center text-sm text-[var(--app-text-soft)]">
            Данные дашборда пока недоступны
          </div>
        )}
      </div>
    </div>
  );
}

